import { AlertCircle, CheckCircle2, CreditCard, QrCode } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  obterPagamentoPorToken,
  processarPagamentoPorToken,
} from '../../api/checkout';
import { aplicarMascara } from '../../components/checkform/CheckoutFormModel';
import { copiarTexto } from '../../shared/utils/copiarTexto';

function formatarHorario(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

function formatarReais(centavos) {
  return (centavos / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

export default function PagamentoLink() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [dados, setDados] = useState(null);
  const [alreadyConfirmed, setAlreadyConfirmed] = useState(false);

  const [metodo, setMetodo] = useState('pix');
  const [cartao, setCartao] = useState({
    numero: '',
    nome: '',
    validade: '',
    cvv: '',
  });
  const [processando, setProcessando] = useState(false);
  const [resultado, setResultado] = useState(null);
  // null = não tentou | true = copiou | false = falhou (mostra o código na tela)
  const [pixCopiado, setPixCopiado] = useState(null);

  async function copiarPix() {
    const copiou = await copiarTexto(resultado?.pixCopiaECola);
    setPixCopiado(copiou);
    if (copiou) setTimeout(() => setPixCopiado(null), 2500);
  }

  useEffect(() => {
    obterPagamentoPorToken(token)
      .then((data) => {
        if (data.alreadyConfirmed) {
          setAlreadyConfirmed(true);
        } else {
          setDados(data);
        }
      })
      .catch(() => setErro('Link inválido ou expirado.'))
      .finally(() => setLoading(false));
  }, [token]);

  function handleCartao(campo, valor) {
    setCartao((prev) => ({ ...prev, [campo]: aplicarMascara(valor, campo) }));
  }

  async function pagar() {
    if (processando) return;
    setProcessando(true);
    setErro(null);

    try {
      let body = { paymentMethod: metodo === 'cartao' ? 'CREDIT_CARD' : 'PIX' };

      if (metodo === 'cartao') {
        const [mes = '', ano = ''] = cartao.validade.split('/');
        body.card = {
          holderName: cartao.nome.trim(),
          number: cartao.numero.replace(/\s/g, ''),
          expiryMonth: mes,
          expiryYear: ano ? `20${ano}` : '',
          ccv: cartao.cvv,
        };
      }

      const data = await processarPagamentoPorToken(token, body);
      setResultado(data);
    } catch (err) {
      setErro(
        err.response?.data?.error ||
          'Erro ao processar pagamento. Tente novamente.',
      );
    } finally {
      setProcessando(false);
    }
  }

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fff5f7',
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            border: '4px solid #ff2d6c',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Pagamento concluído com sucesso
  if (resultado?.paid || resultado?.status === 'CONFIRMED') {
    return (
      <Wrapper>
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <CheckCircle2
            size={56}
            color="#ff2d6c"
            style={{ margin: '0 auto 16px' }}
          />
          <h2 style={S.titulo}>Inscrição confirmada!</h2>
          <p style={S.sub}>
            Parabéns, {dados?.nome}! Você está inscrita no evento. Em breve você
            receberá um email de confirmação.
          </p>
          <button onClick={() => navigate('/')} style={S.btnPrimary}>
            Voltar ao início
          </button>
        </div>
      </Wrapper>
    );
  }

  // PIX gerado
  if (resultado?.pixQrCode || resultado?.pixCopiaECola) {
    return (
      <Wrapper
        evento={dados?.evento}
        nome={dados?.nome}
        horario={dados?.horario}
        valor={dados?.amountInCents}
      >
        <div style={{ textAlign: 'center' }}>
          <h3 style={{ ...S.titulo, marginBottom: 4 }}>
            Total: {formatarReais(dados.amountInCents)}
          </h3>
          <p style={S.sub}>Escaneie o QR Code ou copie o código Pix abaixo.</p>
          <div
            style={{
              margin: '20px auto',
              padding: 8,
              background: '#fff',
              border: '1px solid #f1f5f9',
              borderRadius: 16,
              display: 'inline-block',
            }}
          >
            <img
              src={
                resultado.pixQrCode.startsWith('data:')
                  ? resultado.pixQrCode
                  : `data:image/png;base64,${resultado.pixQrCode}`
              }
              alt="QR Code Pix"
              width={200}
              height={200}
            />
          </div>
          <button
            onClick={copiarPix}
            style={{ ...S.btnPrimary, background: '#fcd34d', color: '#1e293b' }}
          >
            {pixCopiado === true ? '✓ Código copiado' : 'Copiar código Pix'}
          </button>

          {/* Em webview de app a cópia pode ser bloqueada; sem o código à vista
              não há como pagar pelo mesmo celular. */}
          {pixCopiado === false && (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                borderRadius: 16,
                border: '1px solid #fde68a',
                background: '#fffbeb',
                textAlign: 'left',
              }}
            >
              <p style={{ fontSize: 11, fontWeight: 800, color: '#78350f' }}>
                Não foi possível copiar automaticamente. Toque e segure o código
                abaixo:
              </p>
              <p
                style={{
                  marginTop: 8,
                  padding: 10,
                  maxHeight: 112,
                  overflowY: 'auto',
                  wordBreak: 'break-all',
                  borderRadius: 12,
                  border: '1px solid #fde68a',
                  background: '#fff',
                  fontSize: 11,
                  color: '#334155',
                  userSelect: 'all',
                }}
              >
                {resultado.pixCopiaECola}
              </p>
            </div>
          )}
          <p style={{ ...S.sub, marginTop: 16, fontSize: 12 }}>
            Não feche esta página até confirmar o pagamento.
          </p>
        </div>
      </Wrapper>
    );
  }

  // Link inválido ou inscrição já confirmada
  if (erro && !dados) {
    return (
      <Wrapper>
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <AlertCircle
            size={48}
            color="#ef4444"
            style={{ margin: '0 auto 16px' }}
          />
          <h2 style={{ ...S.titulo, color: '#ef4444' }}>{erro}</h2>
          <p style={S.sub}>
            Caso acredite que isso é um erro, entre em contato com a equipe
            Lily.
          </p>
          <button onClick={() => navigate('/')} style={S.btnPrimary}>
            Voltar ao início
          </button>
        </div>
      </Wrapper>
    );
  }

  if (alreadyConfirmed) {
    return (
      <Wrapper>
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <CheckCircle2
            size={48}
            color="#10b981"
            style={{ margin: '0 auto 16px' }}
          />
          <h2 style={S.titulo}>Você já está confirmada!</h2>
          <p style={S.sub}>
            Sua inscrição já foi confirmada. Nos vemos no evento!
          </p>
          <button onClick={() => navigate('/')} style={S.btnPrimary}>
            Voltar ao início
          </button>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper
      evento={dados?.evento}
      nome={dados?.nome}
      horario={dados?.horario}
      valor={dados?.amountInCents}
    >
      {/* Seleção de método */}
      <div style={{ marginBottom: 24 }}>
        <p
          style={{
            fontSize: 11,
            fontWeight: 900,
            color: '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: 10,
          }}
        >
          Forma de pagamento
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          {[
            { id: 'pix', label: 'PIX', Icon: QrCode },
            { id: 'cartao', label: 'Cartão', Icon: CreditCard },
          ].map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setMetodo(id)}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '12px 0',
                borderRadius: 12,
                cursor: 'pointer',
                fontWeight: 800,
                fontSize: 13,
                border: `2px solid ${metodo === id ? '#ff2d6c' : '#e2e8f0'}`,
                background: metodo === id ? '#fff5f7' : '#fff',
                color: metodo === id ? '#ff2d6c' : '#64748b',
                transition: 'all .15s',
              }}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>
      </div>

      {/* Formulário de cartão */}
      {metodo === 'cartao' && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            marginBottom: 24,
          }}
        >
          <Campo label="Nome no cartão">
            <input
              style={S.input}
              value={cartao.nome}
              onChange={(e) => handleCartao('nome', e.target.value)}
              placeholder="NOME SOBRENOME"
            />
          </Campo>
          <Campo label="Número do cartão">
            <input
              style={S.input}
              value={cartao.numero}
              onChange={(e) => handleCartao('numero', e.target.value)}
              placeholder="0000 0000 0000 0000"
              maxLength={19}
            />
          </Campo>
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}
          >
            <Campo label="Validade">
              <input
                style={S.input}
                value={cartao.validade}
                onChange={(e) => handleCartao('validade', e.target.value)}
                placeholder="MM/AA"
                maxLength={5}
              />
            </Campo>
            <Campo label="CVV">
              <input
                style={S.input}
                value={cartao.cvv}
                onChange={(e) => handleCartao('cvv', e.target.value)}
                placeholder="123"
                maxLength={4}
              />
            </Campo>
          </div>
        </div>
      )}

      {erro && (
        <div
          style={{
            background: '#fee2e2',
            color: '#991b1b',
            borderRadius: 10,
            padding: '10px 14px',
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          {erro}
        </div>
      )}

      <button
        onClick={pagar}
        disabled={processando}
        style={{ ...S.btnPrimary, opacity: processando ? 0.6 : 1 }}
      >
        {processando
          ? 'Processando...'
          : metodo === 'pix'
            ? 'Gerar QR Code Pix'
            : 'Pagar com Cartão'}
      </button>
    </Wrapper>
  );
}

function Wrapper({ children, evento, nome, horario, valor }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#fff5f7',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 480,
          background: '#fff',
          borderRadius: 24,
          boxShadow: '0 20px 60px rgba(255,45,108,0.08)',
          overflow: 'hidden',
        }}
      >
        <div style={{ background: '#ff2d6c', padding: '24px 28px' }}>
          <h1
            style={{ color: '#fff', margin: 0, fontSize: 22, fontWeight: 900 }}
          >
            Lily Book Club
          </h1>
          {evento && (
            <p style={{ color: '#ffd6e4', margin: '6px 0 0', fontSize: 14 }}>
              {evento}
            </p>
          )}
        </div>

        {(nome || horario || valor) && (
          <div
            style={{
              background: '#fff5f7',
              borderBottom: '1px solid #fce7f3',
              padding: '16px 28px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 16,
            }}
          >
            {nome && <Info label="Participante" value={nome} />}
            {horario && (
              <Info label="Horário" value={formatarHorario(horario)} />
            )}
            {valor && (
              <Info label="Valor" value={formatarReais(valor)} highlight />
            )}
          </div>
        )}

        <div style={{ padding: '28px' }}>{children}</div>
      </div>
    </div>
  );
}

function Info({ label, value, highlight }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 900,
          color: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 800,
          color: highlight ? '#ff2d6c' : '#0f172a',
          marginTop: 2,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Campo({ label, children }) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        fontSize: 11,
        fontWeight: 900,
        color: '#64748b',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      }}
    >
      {label}
      {children}
    </label>
  );
}

const S = {
  titulo: {
    fontSize: 20,
    fontWeight: 900,
    color: '#0f172a',
    margin: '0 0 8px',
  },
  sub: { fontSize: 14, color: '#64748b', margin: '0 0 24px', lineHeight: 1.6 },
  input: {
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    fontSize: 14,
    color: '#0f172a',
    outline: 'none',
    fontFamily: 'inherit',
  },
  btnPrimary: {
    width: '100%',
    padding: '14px',
    background: '#ff2d6c',
    color: '#fff',
    border: 'none',
    borderRadius: 99,
    fontSize: 13,
    fontWeight: 900,
    cursor: 'pointer',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
};
